import apiClient from './apiClient';
import { unwrapResponse } from './unwrapResponse';
import type {
  ConnectionSearchQueryParams,
  ConnectionSearchUser,
  FriendRequest,
  MemberConnection,
} from '@/types/api';

function withMessage<T>(response: unknown, fallback: string): { message: string; data: T } {
  const obj = response as Record<string, unknown>;
  return {
    message: typeof obj.message === 'string' ? obj.message : fallback,
    data: unwrapResponse<T>(response),
  };
}

export const connectionsApi = {
  searchUsers: async (
    params: ConnectionSearchQueryParams
  ): Promise<{ data: ConnectionSearchUser[]; total: number }> => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 20));
    if (params.name?.trim()) q.set('name', params.name.trim());
    if (params.gender?.trim()) q.set('gender', params.gender.trim());
    if (params.minAge != null) q.set('minAge', String(params.minAge));
    if (params.maxAge != null) q.set('maxAge', String(params.maxAge));

    const rawResponse = await apiClient.get(`/member/connections/search?${q.toString()}`);
    const response = rawResponse as Record<string, unknown>;
    const data = unwrapResponse<ConnectionSearchUser[]>(rawResponse);

    const pg =
      response.pagination && typeof response.pagination === 'object'
        ? (response.pagination as Record<string, unknown>)
        : {};

    const total = (pg.total as number) ?? data.length;
    return { data, total };
  },

  /**
   * Same as searchUsers, but for guests (no auth) — browsing works logged-out; sending a
   * friend request still requires signing in.
   */
  searchUsersPublic: async (
    params: ConnectionSearchQueryParams
  ): Promise<{ data: ConnectionSearchUser[]; total: number }> => {
    const q = new URLSearchParams();
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 20));
    if (params.name?.trim()) q.set('name', params.name.trim());
    if (params.gender?.trim()) q.set('gender', params.gender.trim());
    if (params.minAge != null) q.set('minAge', String(params.minAge));
    if (params.maxAge != null) q.set('maxAge', String(params.maxAge));

    const rawResponse = await apiClient.get(`/member/connections/search-public?${q.toString()}`, {
      omitAuth: true,
    });
    const response = rawResponse as Record<string, unknown>;
    const data = unwrapResponse<ConnectionSearchUser[]>(rawResponse);

    const pg =
      response.pagination && typeof response.pagination === 'object'
        ? (response.pagination as Record<string, unknown>)
        : {};

    const total = (pg.total as number) ?? data.length;
    return { data, total };
  },

  sendRequest: async (userId: string): Promise<{ message: string; data: FriendRequest }> => {
    const res = await apiClient.post('/member/connections/request', { userId });
    return withMessage<FriendRequest>(res, 'Friend request sent.');
  },

  getSentRequests: async (): Promise<FriendRequest[]> => {
    const res = await apiClient.get('/member/connections/requests/sent');
    return unwrapResponse<FriendRequest[]>(res);
  },

  getReceivedRequests: async (): Promise<FriendRequest[]> => {
    const res = await apiClient.get('/member/connections/requests/received');
    return unwrapResponse<FriendRequest[]>(res);
  },

  acceptRequest: async (id: number): Promise<{ message: string; data: FriendRequest }> => {
    const res = await apiClient.post(`/member/connections/requests/${id}/accept`);
    return withMessage<FriendRequest>(res, 'Friend request accepted.');
  },

  declineRequest: async (id: number): Promise<{ message: string; data: FriendRequest }> => {
    const res = await apiClient.post(`/member/connections/requests/${id}/decline`);
    return withMessage<FriendRequest>(res, 'Friend request declined.');
  },

  cancelRequest: async (id: number): Promise<{ message: string }> => {
    const res = await apiClient.delete(`/member/connections/requests/${id}`);
    const obj = res as Record<string, unknown>;
    return { message: typeof obj.message === 'string' ? obj.message : 'Friend request cancelled.' };
  },

  /** `withContact` also returns email/phone/gender (guest-info autofill). */
  listFriends: async (withContact = false): Promise<MemberConnection[]> => {
    const res = await apiClient.get(`/member/connections${withContact ? '?contact=1' : ''}`);
    return unwrapResponse<MemberConnection[]>(res);
  },

  unfriend: async (userId: string): Promise<{ message: string }> => {
    const res = await apiClient.delete(`/member/connections/${userId}`);
    const obj = res as Record<string, unknown>;
    return { message: typeof obj.message === 'string' ? obj.message : 'Friend removed.' };
  },
};

export default connectionsApi;
